import { useEffect, useState } from "react";

/**
 * Checks if the current time is after 2 PM EST/EDT
 * @returns true if current time is after 2 PM Eastern Time
 */
const isAfter2pmEST = (): boolean => {
	const now = new Date();

	// Convert current time to EST/EDT using Intl.
	// This automatically handles EST/EDT transitions.
	const estTime = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/New_York",
		hour: "numeric",
		hour12: false,
	}).format(now);

	const estHour = Number.parseInt(estTime, 10);
	return estHour >= 14; // 14:00 is 2 PM in 24-hour format
};

/**
 * Gets the minimum valid stop date based on business rules
 * After 2 PM EST, the minimum date is the next day
 * Before 2 PM EST, the minimum date is today
 * @returns The minimum selectable date
 */
const getMinStopDate = (): Date => {
	const today = new Date();
	today.setHours(0, 0, 0, 0); // Reset to start of day

	if (isAfter2pmEST()) {
		// Add one day
		today.setDate(today.getDate() + 1);
	}

	return today;
};

/**
 * Hook that returns the minimum valid stop date based on 2 PM EST cutoff
 * Updates in real-time when crossing the 2 PM EST threshold
 * @returns The minimum selectable date for stop dates
 */
export function useMinStopDate(): Date {
	const [minDate, setMinDate] = useState<Date>(getMinStopDate());

	useEffect(() => {
		// Calculate milliseconds until the next potential update
		const now = new Date();
		const estTime = new Intl.DateTimeFormat("en-US", {
			timeZone: "America/New_York",
			hour: "numeric",
			minute: "numeric",
			hour12: false,
		}).format(now);

		const [hoursStr] = estTime.split(":");
		const hours = Number(hoursStr);

		// Calculate milliseconds until 2 PM EST or next day's 12:01 AM EST
		let msUntilNextCheck: number;

		if (hours < 14) {
			// Before 2 PM - check at 2 PM
			const estNow = new Date(
				now.toLocaleString("en-US", { timeZone: "America/New_York" }),
			);
			const next2pm = new Date(estNow);
			next2pm.setHours(14, 0, 0, 0);
			msUntilNextCheck = next2pm.getTime() - estNow.getTime();
		} else {
			// After 2 PM - check at midnight (when new day starts)
			const estNow = new Date(
				now.toLocaleString("en-US", { timeZone: "America/New_York" }),
			);
			const nextMidnight = new Date(estNow);
			nextMidnight.setDate(nextMidnight.getDate() + 1);
			nextMidnight.setHours(0, 1, 0, 0); // 12:01 AM to be safe
			msUntilNextCheck = nextMidnight.getTime() - estNow.getTime();
		}

		const safeMsUntilNextCheck = Math.max(msUntilNextCheck, 1000);

		// Set timeout to update at the calculated time
		const timeoutId = setTimeout(() => {
			const nextMinDate = getMinStopDate();
			setMinDate((previousMinDate) =>
				previousMinDate.getTime() === nextMinDate.getTime()
					? previousMinDate
					: nextMinDate,
			);
		}, safeMsUntilNextCheck);

		return () => clearTimeout(timeoutId);
	}, [minDate]); // Re-run when minDate changes to set next check

	return minDate;
}
